// File: src/utils/sectorAnalyzer.js
// Frontend sector analysis utility for executive orders

// Sector definitions and keywords
const SECTOR_KEYWORDS = {
    Technology: [
        'ai ', 'artificial intelligence', 'data center', 'technology', 'tech ', 
        'software', 'digital', 'cyber', 'internet', 'computing', 'algorithm',
        'machine learning', 'blockchain', 'cryptocurrency', 'cloud', 'platform',
        'semiconductor', 'chip', 'processor', 'innovation', 'startup'
    ],
    
    Energy: [
        'energy', 'oil', 'gas', 'renewable', 'solar', 'wind', 'coal', 'nuclear',
        'pipeline', 'drilling', 'fracking', 'petroleum', 'electricity', 'power',
        'grid', 'utility', 'fossil fuel', 'clean energy', 'battery', 'lng'
    ],
    
    Healthcare: [
        'health', 'medical', 'drug', 'pharmaceut', 'hospital', 'medicare',
        'medicaid', 'vaccine', 'fda', 'disease', 'treatment', 'therapy',
        'clinical', 'patient', 'doctor', 'nurse', 'biotech', 'medicine'
    ],
    
    Financial: [
        'tax', 'bank', 'financial', 'wall street', 'investment', 'securities',
        'trading', 'market', 'economic', 'fiscal', 'budget', 'spending',
        'credit', 'loan', 'mortgage', 'insurance', 'pension', 'fed', 'interest rate'
    ],
    
    Trade: [
        'export', 'import', 'trade', 'tariff', 'china', 'international',
        'foreign', 'border', 'customs', 'commerce', 'wto', 'nafta', 'usmca',
        'supply chain', 'global', 'overseas', 'diplomatic'
    ],
    
    Defense: [
        'defense', 'military', 'security', 'pentagon', 'army', 'navy',
        'air force', 'homeland', 'terrorism', 'weapon', 'missile', 'veteran',
        'intelligence', 'national security', 'strategic', 'warfare'
    ],
    
    Infrastructure: [
        'infrastructure', 'transport', 'highway', 'bridge', 'airport', 'port',
        'railroad', 'construction', 'building', 'permit', 'road', 'tunnel',
        'public works', 'urban planning', 'smart city', 'broadband'
    ],
    
    Agriculture: [
        'agricultur', 'farm', 'food', 'crop', 'livestock', 'rural', 'harvest',
        'grain', 'cattle', 'dairy', 'organic', 'pesticide', 'fertilizer',
        'ranching', 'fishing', 'forestry'
    ],
    
    Education: [
        'education', 'school', 'college', 'university', 'student', 'teacher',
        'academic', 'learning', 'curriculum', 'scholarship', 'research',
        'stem', 'campus', 'degree', 'graduation'
    ],
    
    Labor: [
        'immigration', 'immigrant', 'visa', 'labor', 'worker', 'employment',
        'job', 'workforce', 'union', 'wage', 'salary', 'benefit', 'retirement',
        'unemployment', 'hiring', 'discrimination', 'workplace'
    ],
    
    Environment: [
        'environment', 'climate', 'carbon', 'emission', 'pollution', 'epa',
        'conservation', 'wildlife', 'forest', 'water', 'air quality',
        'sustainability', 'green', 'ecological', 'biodiversity'
    ],
    
    Manufacturing: [
        'manufacturing', 'factory', 'production', 'assembly', 'industrial',
        'automation', 'supply chain', 'made in america', 'reshoring',
        'quality control', 'lean manufacturing', 'automotive'
    ]
};

// Color scheme for frontend display
export const SECTOR_COLORS = {
    'Technology': '#2563eb',      // Blue
    'Energy': '#dc2626',          // Red  
    'Healthcare': '#16a34a',      // Green
    'Financial': '#ca8a04',       // Yellow/Gold
    'Trade': '#9333ea',           // Purple
    'Defense': '#1f2937',         // Dark Gray
    'Infrastructure': '#ea580c',  // Orange
    'Agriculture': '#65a30d',     // Lime
    'Education': '#0891b2',       // Cyan
    'Labor': '#be185d',           // Pink
    'Environment': '#059669',     // Teal
    'Manufacturing': '#7c2d12',   // Brown
    'General': '#6b7280'          // Gray
};

/**
 * Analyzes text content and returns relevant sector tags
 * @param {string} title - The title of the content
 * @param {string} content - The main content text
 * @param {Object} options - Configuration options
 * @returns {Array} Array of sector tag strings
 */
export const getSectorTags = (title = '', content = '', options = {}) => {
    const {
        maxTags = 3,           // Maximum number of tags to return
        minKeywordMatches = 1, // Minimum keyword matches needed for a sector
        caseSensitive = false  // Whether to use case-sensitive matching
    } = options;

    // Combine title and content for analysis
    const text = caseSensitive 
        ? (title + ' ' + content)
        : (title + ' ' + content).toLowerCase();
    
    const sectorScores = {};
    
    // Score each sector based on keyword matches
    Object.entries(SECTOR_KEYWORDS).forEach(([sector, keywords]) => {
        let matches = 0;
        
        keywords.forEach(keyword => {
            const searchKeyword = caseSensitive ? keyword : keyword.toLowerCase();
            
            // Count occurrences of this keyword
            const regex = new RegExp(`\\b${searchKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
            const keywordMatches = (text.match(regex) || []).length;
            
            matches += keywordMatches;
        });
        
        if (matches >= minKeywordMatches) {
            sectorScores[sector] = matches;
        }
    });
    
    // Sort sectors by score and return top matches
    const sortedSectors = Object.entries(sectorScores)
        .sort(([,a], [,b]) => b - a)
        .slice(0, maxTags)
        .map(([sector]) => sector);
    
    // Return sectors or default to General
    return sortedSectors.length > 0 ? sortedSectors : ['General'];
};

/**
 * Get color for a specific sector tag
 * @param {string} sectorTag - The sector tag name
 * @returns {string} Hex color code
 */
export const getSectorColor = (sectorTag) => {
    return SECTOR_COLORS[sectorTag] || SECTOR_COLORS['General'];
};

/**
 * Get all available sector names
 * @returns {Array} Array of all sector names
 */
export const getAllSectors = () => {
    return Object.keys(SECTOR_KEYWORDS);
};